import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { MessageSquarePlus, Bug, Lightbulb, MessageCircle, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { api, apiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const KINDS = [
  { key: "idea", icon: Lightbulb },
  { key: "bug", icon: Bug },
  { key: "other", icon: MessageCircle },
];

// Build-in-public feedback: a one-time welcome popup + an always-on floating
// launcher that opens a short form. Works for anonymous visitors and logged-in users.
export default function FeedbackWidget() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [kind, setKind] = useState("idea");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("sc_welcome_seen")) {
      const id = setTimeout(() => setWelcomeOpen(true), 900);
      return () => clearTimeout(id);
    }
  }, []);

  const dismissWelcome = () => { localStorage.setItem("sc_welcome_seen", "1"); setWelcomeOpen(false); };
  const openForm = () => { dismissWelcome(); setFormOpen(true); };

  const submit = async () => {
    if (!message.trim()) { toast.error(t("feedback.required")); return; }
    setSending(true);
    try {
      await api.post("/feedback", { kind, message: message.trim(), email: email.trim(), page: window.location.pathname });
      toast.success(t("feedback.thanks"));
      setFormOpen(false); setMessage(""); setEmail("");
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  return (
    <>
      <button onClick={() => setFormOpen(true)} data-testid="feedback-launcher" aria-label={t("feedback.button")}
        className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] text-white shadow-lg px-4 py-3 hover:brightness-110 transition">
        <MessageSquarePlus className="h-5 w-5" />
        <span className="hidden sm:inline text-sm font-medium">{t("feedback.button")}</span>
      </button>

      <Dialog open={welcomeOpen} onOpenChange={(v) => !v && dismissWelcome()}>
        <DialogContent className="max-w-md" data-testid="welcome-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-[hsl(var(--accent))]" /> {t("feedback.welcome_title")}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground leading-relaxed">{t("feedback.welcome_body")}</p>
          <div className="flex gap-2 justify-end mt-2">
            <Button variant="ghost" onClick={dismissWelcome} data-testid="welcome-later">{t("feedback.welcome_later")}</Button>
            <Button className="bg-[hsl(var(--primary))] text-white" onClick={openForm} data-testid="welcome-cta">{t("feedback.welcome_cta")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-md" data-testid="feedback-dialog">
          <DialogHeader><DialogTitle>{t("feedback.form_title")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              {KINDS.map(({ key, icon: Icon }) => (
                <button key={key} type="button" onClick={() => setKind(key)} data-testid={`feedback-kind-${key}`}
                  className={`flex flex-col items-center gap-1 rounded-lg border p-2 text-xs transition ${kind === key ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5 text-[hsl(var(--primary))] font-medium" : "border-border text-muted-foreground"}`}>
                  <Icon className="h-4 w-4" /> {t(`feedback.kind_${key}`)}
                </button>
              ))}
            </div>
            <Textarea rows={5} value={message} onChange={(e) => setMessage(e.target.value)} placeholder={t("feedback.placeholder")} data-testid="feedback-message" />
            {!user && <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("feedback.email_optional")} data-testid="feedback-email" />}
            <Button onClick={submit} disabled={sending} className="w-full bg-[hsl(var(--primary))] text-white" data-testid="feedback-submit">{sending ? "…" : t("feedback.send")}</Button>
            <p className="text-[11px] text-muted-foreground text-center">{t("feedback.privacy")}</p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
